// Renders a Copilot answer: paragraphs, headings, lists, simple tables, bold,
// inline code, safe links and [C3] citation chips. Builds React nodes only; no
// HTML from the model is ever injected into the page.

import { Fragment, type ReactNode } from 'react';
import { safeExternalHref } from '@shared/copilot/messages';

export interface CiteHandlers { onCite?: (id: string) => void; known: Set<string> }

function inline(text: string, h: CiteHandlers, keyBase: string): ReactNode[] {
  const out: ReactNode[] = [];
  // Order matters: citations, links, bold, code.
  const re = /\[(C\d{1,3}(?:\s*,\s*C\d{1,3})*)\]|\[([^\]]{1,200})\]\((https?:\/\/[^\s)]{1,2000})\)|\*\*([^*]{1,400})\*\*|`([^`]{1,200})`/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const k = `${keyBase}-${i++}`;
    if (m[1]) {
      const ids = m[1].split(/\s*,\s*/).filter((id) => h.known.has(id));
      out.push(<span key={k} className="whitespace-nowrap">{ids.map((id) => (
        <button key={id} type="button" onClick={() => h.onCite?.(id)} aria-label={`Source ${id.slice(1)}`}
          className="align-super text-[10px] leading-none font-semibold text-primary hover:underline px-0.5">{id.slice(1)}</button>
      ))}</span>);
    } else if (m[2] && m[3]) {
      const href = safeExternalHref(m[3]);
      out.push(href ? <a key={k} href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline break-all">{m[2]}</a> : m[2]);
    } else if (m[4]) {
      out.push(<strong key={k} className="font-semibold text-foreground">{m[4]}</strong>);
    } else if (m[5]) {
      out.push(<code key={k} className="font-mono text-[0.85em] bg-surface-muted px-1 rounded break-all">{m[5]}</code>);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

const isTableRow = (l: string) => /^\s*\|.*\|\s*$/.test(l);
const isSep = (l: string) => /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(l);
const cells = (l: string) => l.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim());

export function RichText({ text, handlers }: { text: string; handlers: CiteHandlers }) {
  const lines = text.replace(/\r/g, '').split('\n');
  const blocks: ReactNode[] = [];
  let i = 0;
  let k = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    if (!line.trim()) { i++; continue; }
    const key = `b${k++}`;
    const head = line.match(/^#{1,4}\s+(.*)$/);
    if (head) {
      blocks.push(<div key={key} className="font-semibold text-foreground mt-2 first:mt-0">{inline(head[1]!, handlers, key)}</div>);
      i++; continue;
    }
    if (isTableRow(line) && i + 1 < lines.length && isSep(lines[i + 1]!)) {
      const header = cells(line);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && isTableRow(lines[i]!)) rows.push(cells(lines[i++]!));
      blocks.push(
        <div key={key} className="overflow-x-auto max-w-full -mx-1 px-1">
          <table className="text-xs border-collapse min-w-full">
            <thead><tr>{header.map((c, j) => <th key={j} className="text-left font-semibold border-b border-border px-1.5 py-1 align-bottom">{inline(c, handlers, `${key}h${j}`)}</th>)}</tr></thead>
            <tbody>{rows.map((r, ri) => <tr key={ri} className="border-b border-border/60">{r.map((c, j) => <td key={j} className="px-1.5 py-1 align-top">{inline(c, handlers, `${key}r${ri}c${j}`)}</td>)}</tr>)}</tbody>
          </table>
        </div>,
      );
      continue;
    }
    if (/^\s*([-*•])\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*([-*•])\s+/.test(lines[i]!)) items.push(lines[i++]!.replace(/^\s*([-*•])\s+/, ''));
      blocks.push(<ul key={key} className="list-disc pl-5 space-y-0.5">{items.map((it, j) => <li key={j}>{inline(it, handlers, `${key}-${j}`)}</li>)}</ul>);
      continue;
    }
    if (/^\s*\d{1,2}[.)]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*\d{1,2}[.)]\s+/.test(lines[i]!)) items.push(lines[i++]!.replace(/^\s*\d{1,2}[.)]\s+/, ''));
      blocks.push(<ol key={key} className="list-decimal pl-5 space-y-0.5">{items.map((it, j) => <li key={j}>{inline(it, handlers, `${key}-${j}`)}</li>)}</ol>);
      continue;
    }
    const para: string[] = [];
    while (i < lines.length && lines[i]!.trim() && !/^#{1,4}\s/.test(lines[i]!) && !/^\s*([-*•]|\d{1,2}[.)])\s+/.test(lines[i]!) && !isTableRow(lines[i]!)) para.push(lines[i++]!);
    blocks.push(<p key={key}>{para.map((p, j) => <Fragment key={j}>{j > 0 && <br />}{inline(p, handlers, `${key}-${j}`)}</Fragment>)}</p>);
  }
  return <div className="space-y-2 break-words [overflow-wrap:anywhere]">{blocks}</div>;
}
